import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import Layout from '../components/Layout';
import ServiceOrderModal from '../components/ServiceOrderModal';

const Schedule = () => {
  const navigate = useNavigate();
  const [viewMode, setViewMode] = useState('daily');
  const getLocalDateStr = (d = new Date()) => {
    const pad = n => String(n).padStart(2,'0');
    return `${d.getFullYear()}-${pad(d.getMonth()+1)}-${pad(d.getDate())}`;
  };
  const [selectedDate, setSelectedDate] = useState(getLocalDateStr());
  const [selectedTechnician, setSelectedTechnician] = useState(null);
  const [scheduleData, setScheduleData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [draggedOrder, setDraggedOrder] = useState(null);
  const [errorMessage, setErrorMessage] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const weeklyGridRef = useRef(null);

  useEffect(() => {
    if (viewMode === 'daily') {
      fetchDailySchedule();
    } else if (viewMode === 'weekly' && selectedTechnician) {
      fetchWeeklySchedule();
    }
  }, [selectedDate, viewMode, selectedTechnician]);

  useEffect(() => {
    if (errorMessage) {
      const timer = setTimeout(() => setErrorMessage(''), 5000);
      return () => clearTimeout(timer);
    }
  }, [errorMessage]);

  // Auto-scroll weekly view — to 22:00 if cross-night WOs exist, else 4 AM
  useEffect(() => {
    if (viewMode === 'weekly' && weeklyGridRef.current && !loading) {
      const hourHeight = 60;
      const hasCrossNight = scheduleData?.orders_by_date &&
        Object.values(scheduleData.orders_by_date).flat()
          .some(o => o.scheduled_end_date && o.scheduled_end_date !== o.scheduled_date && !o.is_continuation);
      const scrollTo = hasCrossNight ? 22 * hourHeight : 4 * hourHeight;
      setTimeout(() => {
        if (weeklyGridRef.current) {
          weeklyGridRef.current.scrollTop = scrollTo;
        }
      }, 100);
    }
  }, [viewMode, loading, scheduleData]);

  const fetchDailySchedule = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/schedule/daily?date=${selectedDate}`);
      setScheduleData(response.data);
      setLoading(false);
    } catch (err) {
      console.error('Error fetching daily schedule:', err);
      setLoading(false);
    }
  };

  const fetchWeeklySchedule = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/schedule/weekly?technician_id=${selectedTechnician.id}&start_date=${selectedDate}`);
      setScheduleData(response.data);
      setLoading(false);
    } catch (err) {
      console.error('Error fetching weekly schedule:', err);
      setLoading(false);
    }
  };

  const handleDateChange = (days) => {
    // Parse date parts directly to avoid UTC offset issues
    const [y, m, d] = selectedDate.split('-').map(Number);
    const newDate = new Date(y, m - 1, d);
    newDate.setDate(newDate.getDate() + days);
    setSelectedDate(getLocalDateStr(newDate));
  };

  const switchToWeekly = (technician) => {
    setSelectedTechnician(technician);
    setViewMode('weekly');
  };

  const switchToDaily = () => {
    setViewMode('daily');
    setSelectedTechnician(null);
  };

  // Check if two time ranges overlap
  const checkTimeOverlap = (start1, end1, start2, end2) => {
    const toMinutes = (timeStr) => {
      const [hours, minutes] = timeStr.split(':').map(Number);
      return hours * 60 + minutes;
    };

    const s1 = toMinutes(start1);
    const e1 = toMinutes(end1);
    const s2 = toMinutes(start2);
    const e2 = toMinutes(end2);

    return (s1 < e2 && e1 > s2);
  };

  // Modal handlers
  const handleOrderClick = (order) => {
    // For continuation blocks, restore original start time before showing modal
    if (order.is_continuation && order.original_start_time) {
      setSelectedOrder({
        ...order,
        scheduled_time_start: order.original_start_time,
        is_continuation: false
      });
    } else {
      setSelectedOrder(order);
    }
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setSelectedOrder(null);
  };

  const handleModalSuccess = () => {
    if (viewMode === 'daily') {
      fetchDailySchedule();
    } else {
      fetchWeeklySchedule();
    }
    handleCloseModal();
  };

  // Validate if the drop would create a conflict
  const validateDrop = (order, targetTechnicianId, targetDate, targetTime) => {
    if (!scheduleData) return { valid: false, message: 'No schedule data' };

    let existingOrders = [];

    if (viewMode === 'weekly') {
      // In weekly view, get orders from orders_by_date for the target date
      existingOrders = scheduleData.orders_by_date?.[targetDate] || [];
    } else {
      // In daily view, get orders from the technician's row
      const targetTech = scheduleData.technicians?.find(t => t.technician.id === targetTechnicianId);
      if (!targetTech) return { valid: false, message: 'Technician not found' };
      existingOrders = targetTech.orders;
      // If dropping to a new date in daily view, skip conflict check
      if (targetDate !== selectedDate) {
        return { valid: true };
      }
    }

    // Calculate the end time based on the order's duration
    const calculateEndTime = (startTime, originalStart, originalEnd) => {
      const toMinutes = (timeStr) => {
        const [hours, minutes] = timeStr.split(':').map(Number);
        return hours * 60 + minutes;
      };

      const duration = toMinutes(originalEnd) - toMinutes(originalStart);
      const newStartMinutes = toMinutes(startTime);
      const newEndMinutes = newStartMinutes + duration;

      const hours = Math.floor(newEndMinutes / 60);
      const minutes = newEndMinutes % 60;

      return String(hours).padStart(2, '0') + ':' + String(minutes).padStart(2, '0') + ':00';
    };

    const newStartTime = targetTime;
    const newEndTime = calculateEndTime(targetTime, order.scheduled_time_start, order.scheduled_time_end);

    // Check conflicts with other orders on target date
    for (const existingOrder of existingOrders) {
      if (existingOrder.id === order.id) continue;

      if (checkTimeOverlap(
        newStartTime.substring(0, 5),
        newEndTime.substring(0, 5),
        existingOrder.scheduled_time_start.substring(0, 5),
        existingOrder.scheduled_time_end.substring(0, 5)
      )) {
        return {
          valid: false,
          message: 'Time conflict with ' + existingOrder.order_number + ' (' + existingOrder.scheduled_time_start.substring(0, 5) + ' - ' + existingOrder.scheduled_time_end.substring(0, 5) + ')'
        };
      }
    }

    return { valid: true };
  };

  // Drag and Drop handlers
  const handleDragStart = (e, order) => {
    setDraggedOrder(order);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDrop = async (e, targetTechnicianId, targetDate, targetTime) => {
    e.preventDefault();
    
    if (!draggedOrder) return;

    // Validate the drop
    const validation = validateDrop(draggedOrder, targetTechnicianId, targetDate, targetTime);

    if (!validation.valid) {
      setErrorMessage(validation.message);
      setDraggedOrder(null);
      return;
    }

    // Calculate the end time based on original duration
    const toMinutes = (timeStr) => {
      const [hours, minutes] = timeStr.split(':').map(Number);
      return hours * 60 + minutes;
    };

    const duration = toMinutes(draggedOrder.scheduled_time_end) - toMinutes(draggedOrder.scheduled_time_start);
    const newStartMinutes = toMinutes(targetTime);
    const newEndMinutes = newStartMinutes + duration;

    const newEndHours = Math.floor(newEndMinutes / 60);
    const newEndMins = newEndMinutes % 60;
    const newEndTime = `${String(newEndHours).padStart(2, '0')}:${String(newEndMins).padStart(2, '0')}:00`;

    try {
      await api.patch(`/schedule/order/${draggedOrder.id}`, {
        technician_id: targetTechnicianId,
        scheduled_date: targetDate || draggedOrder.scheduled_date,
        scheduled_time_start: targetTime + ':00',
        scheduled_time_end: newEndTime
      });

      // Refresh schedule
      if (viewMode === 'daily') {
        fetchDailySchedule();
      } else {
        fetchWeeklySchedule();
      }

      setDraggedOrder(null);
      setErrorMessage('');
    } catch (err) {
      console.error('Error updating order:', err);
      setErrorMessage('Error moving order: ' + (err.response?.data?.message || err.message));
    }
  };

  // Generate 24-hour time slots with 15-minute increments
  const getTimeSlots = () => {
    const slots = [];
    for (let hour = 0; hour < 24; hour++) {
      for (let minute = 0; minute < 60; minute += 15) {
        slots.push(`${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}`);
      }
    }
    return slots;
  };

  // Group time slots by hour for better display
  const getHourlySlots = () => {
    const hours = [];
    for (let hour = 0; hour < 24; hour++) {
      hours.push(String(hour).padStart(2, '0'));
    }
    return hours;
  };

  const formatDate = (dateStr) => {
    // Add T12:00:00 to avoid UTC midnight shift
    const date = new Date(dateStr + 'T12:00:00');
    return date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
  };

  const getStatusColor = (status) => {
    const colors = {
      scheduled: '#2196F3',
      in_progress: '#FFD600',
      completed: '#4CAF50',
      cancelled: '#616161'
    };
    return colors[status] || '#9E9E9E';
  };


  const getOrderPosition = (order) => {
    const toMinutes = (timeStr) => {
      if (!timeStr) return 0;
      const [hours, minutes] = timeStr.split(':').map(Number);
      return hours * 60 + minutes;
    };
    const startMinutes = toMinutes(order.scheduled_time_start);
    let endMinutes = toMinutes(order.scheduled_time_end);
    const isCrossNight = !order.is_continuation &&
      order.scheduled_end_date && order.scheduled_end_date !== order.scheduled_date;
    if (order.is_continuation) {
      endMinutes = toMinutes(order.scheduled_time_end);
    } else if (isCrossNight) {
      endMinutes = 1439;
    } else if (endMinutes <= startMinutes) {
      endMinutes = 1439;
    }
    const duration = Math.max(15, endMinutes - startMinutes);
    return {
      left: `${(startMinutes / (24 * 60)) * 100}%`,
      width: `${(duration / (24 * 60)) * 100}%`
    };
  };
  if (loading) {
    return (
      <div style={{ padding: '20px', textAlign: 'center' }}>
        <div style={{ fontSize: '24px', color: '#2D5C3F' }}>Loading schedule...</div>
      </div>
    );
  }

  return (
    <Layout>
    <div style={{ padding: '20px', backgroundColor: '#F5F5F5', minHeight: '100vh' }}>
      {/* Error Message */}
      {errorMessage && (
        <div style={{
          position: 'fixed',
          top: '80px',
          left: '50%',
          transform: 'translateX(-50%)',
          backgroundColor: '#F44336',
          color: 'white',
          padding: '15px 30px',
          borderRadius: '8px',
          boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
          zIndex: 1000,
          fontSize: '14px',
          fontWeight: 'bold',
          maxWidth: '600px'
        }}>
          ⚠️ {errorMessage}
        </div>
      )}

      {/* Header */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '20px',
        backgroundColor: 'white',
        padding: '20px',
        borderRadius: '8px',
        boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
      }}>
        <div>
          <h1 style={{ color: '#2D5C3F', margin: 0, marginBottom: '5px' }}>
            {viewMode === 'daily' ? 'Daily Schedule' : `Weekly Schedule - ${selectedTechnician?.first_name} ${selectedTechnician?.last_name}`}
          </h1>
          <p style={{ color: '#666', margin: 0 }}>
            {formatDate(selectedDate)}
          </p>
        </div>

        <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
          <button
            onClick={() => handleDateChange(-1)}
            style={{
              padding: '10px 15px',
              backgroundColor: 'white',
              border: '2px solid #2D5C3F',
              borderRadius: '4px',
              cursor: 'pointer',
              fontWeight: 'bold',
              color: '#2D5C3F'
            }}
          >
            ← Prev
          </button>

          <input
            type="date"
            value={selectedDate}
            onChange={(e) => setSelectedDate(e.target.value)}
            style={{
              padding: '10px',
              border: '2px solid #ddd',
              borderRadius: '4px',
              fontSize: '14px'
            }}
          />

          <button
            onClick={() => handleDateChange(1)}
            style={{
              padding: '10px 15px',
              backgroundColor: 'white',
              border: '2px solid #2D5C3F',
              borderRadius: '4px',
              cursor: 'pointer',
              fontWeight: 'bold',
              color: '#2D5C3F'
            }}
          >
            Next →
          </button>

          {viewMode === 'weekly' && (
            <button
              onClick={switchToDaily}
              style={{
                padding: '10px 20px',
                backgroundColor: '#2D5C3F',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
                fontWeight: 'bold'
              }}
            >
              ← Back to Daily
            </button>
          )}


        </div>
      </div>


      {/* Status Legend */}
      <div style={{
        display: 'flex',
        gap: '20px',
        alignItems: 'center',
        marginBottom: '10px',
        padding: '8px 15px',
        backgroundColor: 'white',
        borderRadius: '6px',
        boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
        fontSize: '12px'
      }}>
        <span style={{ fontWeight: 'bold', color: '#666' }}>Status:</span>
        <span style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
          <span style={{ width: '14px', height: '14px', borderRadius: '3px', backgroundColor: '#2196F3', display: 'inline-block' }}></span>
          Scheduled
        </span>
        <span style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
          <span style={{ width: '14px', height: '14px', borderRadius: '3px', backgroundColor: '#FFD600', display: 'inline-block', border: '1px solid #ccc' }}></span>
          In Progress
        </span>
        <span style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
          <span style={{ width: '14px', height: '14px', borderRadius: '3px', backgroundColor: '#4CAF50', display: 'inline-block' }}></span>
          Completed
        </span>
        <span style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
          <span style={{ width: '14px', height: '14px', borderRadius: '3px', backgroundColor: '#616161', display: 'inline-block' }}></span>
          Cancelled
        </span>
      </div>
      {/* Daily View with Timeline */}
      {viewMode === 'daily' && scheduleData && (
        <div style={{
          backgroundColor: 'white',
          borderRadius: '8px',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
          overflow: 'hidden'
        }}>
          {/* Time Header */}
          <div style={{
            display: 'flex',
            borderBottom: '2px solid #ddd',
            backgroundColor: '#F5F5F5'
          }}>
            <div style={{
              width: '150px',
              padding: '15px',
              fontWeight: 'bold',
              borderRight: '2px solid #ddd',
              flexShrink: 0
            }}>
              Technician
            </div>
            <div style={{ flex: 1, position: 'relative', height: '50px' }}>
              {getHourlySlots().map(hour => (
                <div
                  key={hour}
                  style={{
                    position: 'absolute',
                    left: `${(parseInt(hour) / 24) * 100}%`,
                    top: 0,
                    bottom: 0,
                    borderLeft: '1px solid #ddd',
                    paddingLeft: '5px',
                    paddingTop: '15px',
                    fontSize: '12px',
                    color: '#666',
                    fontWeight: 'bold'
                  }}
                >
                  {hour}:00
                </div>
              ))}
            </div>
          </div>

          {/* Technicians Rows */}
          {scheduleData.technicians?.map(techData => (
            <div
              key={techData.technician.id}
              style={{
                display: 'flex',
                borderBottom: '1px solid #eee',
                minHeight: '80px'
              }}
            >
              <div
                onDoubleClick={() => switchToWeekly(techData.technician)}
                style={{
                  width: '150px',
                  padding: '15px',
                  fontWeight: 'bold',
                  color: '#2D5C3F',
                  borderRight: '2px solid #eee',
                  cursor: 'pointer',
                  flexShrink: 0,
                  display: 'flex',
                  alignItems: 'center'
                }}
                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#F0F7F4'}
                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'white'}
              >
                {techData.technician.first_name} {techData.technician.last_name}
                {(() => {
                  const conflicts = techData.orders.filter(o => o.scheduling_conflict);
                  return conflicts.length > 0 ? (
                    <span title={`${conflicts.length} scheduling conflict(s) — review required`}
                      style={{ marginLeft:6, background:'#DC2626', color:'white',
                        borderRadius:10, padding:'1px 7px', fontSize:10,
                        fontWeight:700, cursor:'default', display:'inline-block' }}>
                      ⚠ {conflicts.length}
                    </span>
                  ) : null;
                })()}
              </div>
              
              <div
                style={{
                  flex: 1,
                  position: 'relative',
                  padding: '10px 0'
                }}
                onDragOver={handleDragOver}
              >
                {/* 15-minute drop zones */}
                {getTimeSlots().map(timeSlot => (
                  <div
                    key={timeSlot}
                    style={{
                      position: 'absolute',
                      left: `${(parseInt(timeSlot.split(':')[0]) * 60 + parseInt(timeSlot.split(':')[1])) / (24 * 60) * 100}%`,
                      width: `${(15 / (24 * 60)) * 100}%`,
                      height: '100%',
                      borderLeft: timeSlot.endsWith(':00') ? '1px solid #eee' : '1px dashed #f5f5f5'
                    }}
                    onDrop={(e) => handleDrop(e, techData.technician.id, selectedDate, timeSlot)}
                  />
                ))}

                {/* Orders */}
                {techData.orders.map(order => {
                  const position = getOrderPosition(order);
                  return (
                    <div
                      key={order.id}
                      draggable
                      onDragStart={(e) => handleDragStart(e, order)}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleOrderClick(order);
                      }}
                      style={{
                        position: 'absolute',
                        left: position.left,
                        width: position.width,
                        backgroundColor: getStatusColor(order.status),
                        color: 'white',
                        padding: '8px',
                        borderRadius: '4px',
                        cursor: 'pointer',
                        fontSize: '12px',
                        boxShadow: order.scheduling_conflict
                          ? '0 0 0 2px #DC2626, 0 2px 8px rgba(220,38,38,0.4)'
                          : '0 2px 4px rgba(0,0,0,0.2)',
                        top: '10px',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        border: order.scheduling_conflict ? '2px solid #DC2626' : 'none',
                        outline: order.scheduling_conflict ? '1px dashed #FCA5A5' : 'none'
                      }}
                      title={order.is_continuation
                        ? `${order.order_number} - ${order.facility_name}\n↪ Continuation: 00:00 - ${order.scheduled_time_end?.substring(0,5)} (started ${order.original_start_time?.substring(0,5)} prev. day)`
                        : `${order.order_number} - ${order.facility_name}\n${order.scheduled_time_start?.substring(0, 5)} - ${order.scheduled_time_end?.substring(0, 5)}`}
                    >
                      <div style={{ fontWeight: 'bold', marginBottom: '2px' }}>
                        {order.order_number}
                      </div>
                      <div style={{ fontSize: '11px', opacity: 0.9 }}>
                        {order.facility_name}
                      </div>
                      {order.is_continuation && (
                        <div style={{ fontSize:'9px', opacity:0.8, fontStyle:'italic' }}>
                          ↪ cont. from prev. day
                        </div>
                      )}
                      {!order.is_continuation && order.scheduled_end_date && order.scheduled_end_date !== order.scheduled_date && (
                        <div style={{ fontSize:'9px', opacity:0.8, fontStyle:'italic' }}>
                          ↗ continues next day
                        </div>
                      )}
                      <div style={{ fontSize: '10px', opacity: 0.8, marginTop: '2px' }}>
                        {order.scheduled_time_start?.substring(0, 5)} - {order.scheduled_time_end?.substring(0, 5)}
                      </div>
                      {order.scheduling_conflict && (
                        <div style={{ fontSize:'9px', fontWeight:700,
                          background:'rgba(220,38,38,0.9)', borderRadius:3,
                          padding:'1px 4px', marginTop:2, display:'inline-block' }}>
                          ⚠ CONFLICT
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          ))}

          {/* Unassigned Row */}
          {scheduleData.unassigned?.length > 0 && (
            <div style={{
              display: 'flex',
              backgroundColor: '#FFF3E0',
              borderTop: '3px solid #FFD600',
              minHeight: '100px'
            }}>
              <div style={{
                width: '150px',
                padding: '15px',
                fontWeight: 'bold',
                color: '#FFD600',
                borderRight: '2px solid #eee',
                flexShrink: 0,
                display: 'flex',
                alignItems: 'center'
              }}>
                ⚠️ Unassigned
              </div>
              <div style={{ flex: 1, padding: '10px', display: 'flex', flexWrap: 'wrap', gap: '10px', alignItems: 'flex-start' }}>
                {scheduleData.unassigned.map(order => (
                  <div
                    key={order.id}
                    draggable
                    onDragStart={(e) => handleDragStart(e, order)}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleOrderClick(order);
                    }}
                    style={{
                      backgroundColor: '#FFD600',
                      color: 'white',
                      padding: '10px',
                      borderRadius: '4px',
                      cursor: 'pointer',
                      fontSize: '13px',
                      minWidth: '200px',
                      boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
                    }}
                  >
                    <div style={{ fontWeight: 'bold', marginBottom: '5px' }}>
                      {order.order_number}
                    </div>
                    <div style={{ fontSize: '12px' }}>
                      {order.facility_name}
                    </div>
                    <div style={{ fontSize: '11px', opacity: 0.9 }}>
                      {order.scheduled_time_start ? 
                        `${order.scheduled_time_start.substring(0, 5)} - ${order.scheduled_time_end.substring(0, 5)}` 
                        : 'No time assigned'}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Weekly View - 7 Day Technician Schedule */}
      {viewMode === 'weekly' && scheduleData && (
        <div style={{
          backgroundColor: 'white',
          borderRadius: '8px',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
          overflow: 'hidden'
        }}>
          {/* Week Day Headers */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: '70px repeat(7, 1fr)',
            borderBottom: '2px solid #ddd',
            backgroundColor: '#F5F5F5'
          }}>
            <div style={{
              padding: '10px 5px',
              fontWeight: 'bold',
              borderRight: '1px solid #ddd',
              fontSize: '11px',
              color: '#666',
              textAlign: 'center'
            }}>
              Time
            </div>
            {scheduleData.orders_by_date && Object.keys(scheduleData.orders_by_date).map((dateStr, idx) => {
              const d = new Date(dateStr + 'T12:00:00');
              const isToday = dateStr === getLocalDateStr();
              const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
              const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
              return (
                <div
                  key={dateStr}
                  style={{
                    padding: '10px 5px',
                    fontWeight: 'bold',
                    borderRight: idx < 6 ? '1px solid #ddd' : 'none',
                    textAlign: 'center',
                    backgroundColor: isToday ? '#E8F5E9' : 'transparent',
                    borderBottom: isToday ? '3px solid #2D5C3F' : 'none'
                  }}
                >
                  <div style={{ fontSize: '12px', color: isToday ? '#2D5C3F' : '#666' }}>
                    {dayNames[d.getDay()]} {monthNames[d.getMonth()].substring(0,2)}/{String(d.getDate()).padStart(2,'0')}
                  </div>
                </div>
              );
            })}
          </div>

          {/* Time Grid */}
          <div ref={weeklyGridRef} style={{ maxHeight: 'calc(100vh - 220px)', overflowY: 'auto' }}>
            {Array.from({ length: 24 }, (_, i) => i).map(hour => (
              <div
                key={hour}
                style={{
                  display: 'grid',
                  gridTemplateColumns: '70px repeat(7, 1fr)',
                  borderBottom: '1px solid #f0f0f0',
                  minHeight: '60px'
                }}
              >
                {/* Hour label */}
                <div style={{
                  padding: '5px',
                  fontSize: '11px',
                  color: '#999',
                  borderRight: '1px solid #ddd',
                  textAlign: 'center',
                  fontWeight: 'bold'
                }}>
                  {hour === 0 ? '12 AM' : hour < 12 ? hour + ' AM' : hour === 12 ? '12 PM' : (hour - 12) + ' PM'}
                </div>

                {/* Day columns */}
                {scheduleData.orders_by_date && Object.keys(scheduleData.orders_by_date).map((dateStr, dayIdx) => {
                  const dayOrders = scheduleData.orders_by_date[dateStr] || [];
                  const hourOrders = dayOrders.filter(order => {
                    const startHour = parseInt(order.scheduled_time_start?.substring(0, 2));
                    return startHour === hour;
                  });
                  // Calculate block height — handles cross-midnight WOs
                  const isCrossNight = (order) =>
                    !order.is_continuation &&
                    order.scheduled_end_date && order.scheduled_end_date !== order.scheduled_date;

                  const getBlockHeight = (order) => {
                    const startH = parseInt(order.scheduled_time_start?.substring(0, 2) || '0');
                    const startM = parseInt(order.scheduled_time_start?.substring(3, 5) || '0');
                    const rawEndH = parseInt(order.scheduled_time_end?.substring(0, 2) || '0');
                    const rawEndM = parseInt(order.scheduled_time_end?.substring(3, 5) || '0');
                    const startMin = startH * 60 + startM;
                    let durationMin;
                    if (order.is_continuation) {
                      // Continuation: use REAL end time (00:00 → actual end)
                      durationMin = rawEndH * 60 + rawEndM;
                    } else if (isCrossNight(order)) {
                      // Starting block: from start → 23:59
                      durationMin = 1439 - startMin;
                    } else {
                      const endMin = rawEndH * 60 + rawEndM;
                      durationMin = endMin - startMin;
                      if (durationMin <= 0) durationMin = 60;
                    }
                    return Math.max(30, (durationMin / 60) * 60) + 'px';
                  };
                  const isToday = dateStr === getLocalDateStr();

                  return (
                    <div
                      key={dateStr}
                      style={{
                        borderRight: dayIdx < 6 ? '1px solid #f0f0f0' : 'none',
                        padding: '2px',
                        position: 'relative',
                        backgroundColor: isToday ? '#FAFFF8' : 'transparent',
                        overflow: 'visible',
                        minWidth: 0
                      }}
                      onDragOver={handleDragOver}
                      onDrop={(e) => handleDrop(e, selectedTechnician.id, dateStr, String(hour).padStart(2, '0') + ':00')}
                    >
                      {hourOrders.map(order => (
                        <div
                          key={order.id}
                          draggable
                          onDragStart={(e) => handleDragStart(e, order)}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleOrderClick(order);
                          }}
                          style={{
                            backgroundColor: getStatusColor(order.status),
                            color: 'white',
                            padding: '4px 6px',
                            borderRadius: order.is_continuation
                              ? '0 0 4px 4px'
                              : isCrossNight(order) ? '4px 4px 0 0' : '4px',
                            cursor: 'pointer',
                            fontSize: '11px',
                            boxShadow: order.scheduling_conflict
                              ? '0 0 0 2px #DC2626, 0 2px 8px rgba(220,38,38,0.5)'
                              : '0 1px 3px rgba(0,0,0,0.2)',
                            overflow: 'hidden',
                            position: 'absolute',
                            top: 0,
                            left: '2px',
                            right: '2px',
                            height: getBlockHeight(order),
                            zIndex: order.scheduling_conflict ? 10 : 5,
                            borderBottom: isCrossNight(order) && !order.is_continuation
                              ? '3px dashed rgba(255,255,255,0.7)'
                              : order.scheduling_conflict ? '3px solid #DC2626' : 'none',
                            borderTop: order.is_continuation
                              ? '3px dashed rgba(255,255,255,0.7)' : 'none',
                            opacity: order.is_continuation ? 0.85 : 1
                          }}
                          title={
                            order.order_number + ' - ' + order.facility_name + '\n' +
                            (order.is_continuation
                              ? 'Continuation: 00:00 → ' + (order.scheduled_time_end?.substring(0,5) || '')
                              : (order.scheduled_time_start?.substring(0,5) || '') + ' → ' + (order.scheduled_time_end?.substring(0,5) || '') +
                                (isCrossNight(order) ? ' (continues next day)' : ''))
                          }
                        >
                          <div style={{ fontWeight: 'bold', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                            {order.facility_name}
                          </div>
                          <div style={{ fontSize: '10px', opacity: 0.9 }}>
                            {order.facility_address}
                          </div>
                          <div style={{ fontSize: '10px', opacity: 0.9 }}>
                            {order.is_continuation
                              ? <>↪ 00:00 – {order.scheduled_time_end?.substring(0,5)}</>
                              : isCrossNight(order)
                                ? <>{order.scheduled_time_start?.substring(0,5)} – 00:00 ↗</>
                                : <>{order.scheduled_time_start?.substring(0,5)} – {order.scheduled_time_end?.substring(0,5)}</>
                            }
                          </div>
                          {order.is_continuation && (
                            <div style={{ fontSize:'9px', opacity:0.7, fontStyle:'italic' }}>
                              ← started {order.original_start_time?.substring(0,5)} prev. day
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  );
                })}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Service Order Modal */}
      {showModal && selectedOrder && (
        <ServiceOrderModal
          isOpen={showModal}
          order={selectedOrder}
          facilityId={selectedOrder.facility_id}
          facilityName={selectedOrder.facility_name}
          clientName={selectedOrder.client_name}
          onClose={handleCloseModal}
          onSuccess={handleModalSuccess}
        />
      )}
    </div>
    </Layout>
  );
};

export default Schedule;
